import { z } from "@core/zod";

import type { ReadonlyRecord } from "../types/index.ts";

import type { LoggerContract } from "./LoggerContract.ts";

export const LogLevelSchema = z.enum([
  "DEBUG",
  "ERROR",
  "INFO",
  "NEVER",
  "WARN",
]);

export type LogLevel = z.infer<typeof LogLevelSchema>;

const PRIORITY: ReadonlyRecord<LogLevel, number> = {
  DEBUG: 4,
  ERROR: 1,
  INFO: 3,
  NEVER: 0,
  WARN: 2,
};

export abstract class BaseLogger implements LoggerContract {
  public readonly __typeTag = "LoggerContract";

  private readonly logLevel: LogLevel;

  public constructor(logLevel: LogLevel) {
    this.logLevel = logLevel;
  }

  public debug(...args: readonly unknown[]): void {
    if (this.shouldLog("DEBUG")) {
      this.debugOutput(...args);
    }
  }

  public error(...args: readonly unknown[]): void {
    if (this.shouldLog("ERROR")) {
      this.errorOutput(...args);
    }
  }

  public info(...args: readonly unknown[]): void {
    if (this.shouldLog("INFO")) {
      this.infoOutput(...args);
    }
  }

  public warn(...args: readonly unknown[]): void {
    if (this.shouldLog("WARN")) {
      this.warnOutput(...args);
    }
  }

  protected abstract debugOutput(...args: readonly unknown[]): void;

  protected abstract errorOutput(...args: readonly unknown[]): void;

  protected abstract infoOutput(...args: readonly unknown[]): void;

  protected abstract warnOutput(...args: readonly unknown[]): void;

  private shouldLog(targetLevel: LogLevel): boolean {
    return PRIORITY[this.logLevel] >= PRIORITY[targetLevel];
  }
}
